// src/theme/muiTheme.js
import { createTheme } from '@mui/material/styles';

// Design tokens matching our global CSS
const designTokens = {
  // Color Palette
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  secondary: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },
  accent: {
    orange: '#fb923c',
    purple: '#a855f7',
    pink: '#ec4899',
    teal: '#14b8a6',
    yellow: '#fbbf24',
  },
  semantic: {
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  // Typography
  fontFamily: {
    primary: '"Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif',
    secondary: '"Open Sans", "Helvetica Neue", Arial, sans-serif',
    mono: '"SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace',
  },
  // Spacing (converted to MUI spacing scale)
  spacing: {
    xs: 4,   // 4px
    sm: 8,   // 8px
    md: 16,  // 16px
    lg: 24,  // 24px
    xl: 32,  // 32px
    xxl: 48, // 48px
  },
  // Border Radius
  borderRadius: {
    sm: 2,
    md: 6,
    lg: 8,
    xl: 12,
    xxl: 16,
    full: 9999,
  },
  // Shadows
  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

// Create the Material-UI theme
const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: designTokens.primary[600],
      light: designTokens.primary[400],
      dark: designTokens.primary[800],
      contrastText: '#ffffff',
    },
    secondary: {
      main: designTokens.secondary[500],
      light: designTokens.secondary[300],
      dark: designTokens.secondary[700],
      contrastText: '#ffffff',
    },
    error: {
      main: designTokens.semantic.error,
      light: '#fecaca',
      dark: '#dc2626',
    },
    warning: {
      main: designTokens.semantic.warning,
      light: '#fef3c7',
      dark: '#d97706',
    },
    info: {
      main: designTokens.semantic.info,
      light: '#dbeafe',
      dark: '#1d4ed8',
    },
    success: {
      main: designTokens.semantic.success,
      light: '#bbf7d0',
      dark: '#15803d',
    },
    grey: {
      50: designTokens.neutral[50],
      100: designTokens.neutral[100],
      200: designTokens.neutral[200],
      300: designTokens.neutral[300],
      400: designTokens.neutral[400],
      500: designTokens.neutral[500],
      600: designTokens.neutral[600],
      700: designTokens.neutral[700],
      800: designTokens.neutral[800],
      900: designTokens.neutral[900],
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#6b7280',
      disabled: '#d1d5db',
    },
    divider: '#e5e7eb',
    action: {
      active: designTokens.primary[600],
      hover: 'rgba(59, 130, 246, 0.04)',
      selected: 'rgba(59, 130, 246, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
  },
  typography: {
    fontFamily: designTokens.fontFamily.secondary,
    h1: {
      fontFamily: designTokens.fontFamily.primary,
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontFamily: designTokens.fontFamily.primary,
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: 1.25,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: designTokens.fontFamily.primary,
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.25,
    },
    h4: {
      fontFamily: designTokens.fontFamily.primary,
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: designTokens.fontFamily.primary,
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.25,
    },
    h6: {
      fontFamily: designTokens.fontFamily.primary,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.25,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.625,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: designTokens.neutral[500],
    },
    button: {
      fontFamily: designTokens.fontFamily.primary,
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'none',
      letterSpacing: '0.025em',
    },
  },
  shape: {
    borderRadius: designTokens.borderRadius.lg,
  },
  spacing: 8, // Base spacing unit (8px)
  shadows: [
    'none',
    designTokens.shadows.xs,
    designTokens.shadows.sm,
    designTokens.shadows.md,
    designTokens.shadows.lg,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
  ],
  components: {
    // Button Components
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '10px 20px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: designTokens.shadows.sm,
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease',
        },
        contained: {
          background: `linear-gradient(135deg, ${designTokens.primary[500]} 0%, ${designTokens.primary[600]} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${designTokens.primary[600]} 0%, ${designTokens.primary[700]} 100%)`,
            boxShadow: designTokens.shadows.md,
          },
        },
        outlined: {
          borderColor: designTokens.neutral[300],
          '&:hover': {
            borderColor: designTokens.neutral[400],
            backgroundColor: designTokens.neutral[50],
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
          },
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '0.75rem',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
        },
      },
    },
    
    // Card Components
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.xxl,
          boxShadow: designTokens.shadows.sm,
          border: `1px solid ${designTokens.neutral[200]}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: designTokens.shadows.md,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },
    
    // Paper Components
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.xl,
          boxShadow: designTokens.shadows.sm,
        },
        elevation0: {
          boxShadow: 'none',
          border: `1px solid ${designTokens.neutral[200]}`,
        },
        elevation1: {
          boxShadow: designTokens.shadows.sm,
        },
        elevation2: {
          boxShadow: designTokens.shadows.md,
        },
        elevation3: {
          boxShadow: designTokens.shadows.lg,
        },
      },
    },
    
    // Input Components
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borderRadius.lg,
            backgroundColor: designTokens.neutral[50],
            '& fieldset': {
              borderColor: designTokens.neutral[300],
            },
            '&:hover fieldset': {
              borderColor: designTokens.neutral[400],
            },
            '&.Mui-focused fieldset': {
              borderColor: designTokens.primary[500],
              boxShadow: `0 0 0 3px ${designTokens.primary[100]}`,
            },
          },
        },
      },
    },
    
    // Progress Components
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.full,
          height: 6,
          backgroundColor: designTokens.neutral[200],
        },
        bar: {
          borderRadius: designTokens.borderRadius.full,
          background: `linear-gradient(135deg, ${designTokens.primary[500]} 0%, ${designTokens.primary[600]} 100%)`,
        },
      },
    },
    
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: designTokens.primary[500],
        },
      },
    },
    
    // Chip Components
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.full,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            backgroundColor: designTokens.primary[100],
            color: designTokens.primary[800],
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: designTokens.secondary[100],
            color: designTokens.secondary[800],
          },
          '&.MuiChip-colorSuccess': {
            backgroundColor: designTokens.semantic.success + '20',
            color: designTokens.secondary[800],
          },
          '&.MuiChip-colorError': {
            backgroundColor: '#fecaca',
            color: '#dc2626',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: '#fef3c7',
            color: '#d97706',
          },
        },
      },
    },
    
    // AppBar Components - FIXED TO BE WHITE
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1f2937',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid #e2e8f0',
          '& .MuiToolbar-root': {
            color: '#1f2937',
          },
          '& .MuiIconButton-root': {
            color: '#64748b',
          },
          '& .MuiTypography-root': {
            color: '#1f2937',
          },
        },
        colorPrimary: {
          backgroundColor: '#ffffff',
          color: '#1f2937',
        },
        colorSecondary: {
          backgroundColor: '#ffffff',
          color: '#1f2937',
        },
        colorInherit: {
          backgroundColor: '#ffffff',
          color: '#1f2937',
        },
        colorDefault: {
          backgroundColor: '#ffffff',
          color: '#1f2937',
        },
        colorTransparent: {
          backgroundColor: '#ffffff',
          color: '#1f2937',
        },
      },
    },
    
    // Drawer Components
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: designTokens.neutral[50],
          borderRight: `1px solid ${designTokens.neutral[200]}`,
        },
      },
    },
    
    // List Components
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: designTokens.neutral[100],
          },
        },
      },
    },
    
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: designTokens.neutral[100],
          },
          '&.Mui-selected': {
            backgroundColor: designTokens.primary[100],
            color: designTokens.primary[800],
            '&:hover': {
              backgroundColor: designTokens.primary[200],
            },
          },
        },
      },
    },
    
    // Dialog Components
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: designTokens.borderRadius.xxl,
          boxShadow: designTokens.shadows.xl,
        },
      },
    },
    
    // Tabs Components
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: designTokens.neutral[200],
          borderRadius: designTokens.borderRadius.full,
          padding: '4px',
          minHeight: 44,
        },
        indicator: {
          backgroundColor: '#ffffff',
          height: 'calc(100% - 8px)',
          top: 4,
          borderRadius: designTokens.borderRadius.lg, // Use lg instead of full for better visual
          boxShadow: designTokens.shadows.sm,
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: designTokens.neutral[600],
          zIndex: 1,
          borderRadius: designTokens.borderRadius.lg, // Round the individual tabs
          margin: '2px',
          transition: 'all 0.2s ease',
          '&.Mui-selected': {
            color: designTokens.neutral[900],
            fontWeight: 600,
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            borderRadius: designTokens.borderRadius.lg,
          },
          // Remove any focus ring styling that might cause square shadows
          '&:focus': {
            outline: 'none',
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${designTokens.primary[500]}`,
            outlineOffset: '2px',
            borderRadius: designTokens.borderRadius.lg,
          },
        },
      },
    },
    
    // Tooltip Components
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: designTokens.neutral[800],
          borderRadius: designTokens.borderRadius.lg,
          fontSize: '0.75rem',
          padding: '8px 12px',
        },
      },
    },
    
    // Snackbar Components
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiSnackbarContent-root': {
            backgroundColor: designTokens.neutral[800],
            borderRadius: designTokens.borderRadius.lg,
          },
        },
      },
    },
    
    // Alert Components
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          border: 'none',
        },
        standardSuccess: {
          backgroundColor: designTokens.secondary[50],
          color: designTokens.secondary[800],
        },
        standardError: {
          backgroundColor: '#fef2f2',
          color: '#dc2626',
        },
        standardWarning: {
          backgroundColor: '#fffbeb',
          color: '#d97706',
        },
        standardInfo: {
          backgroundColor: designTokens.primary[50],
          color: designTokens.primary[800],
        },
      },
    },
    
    // Menu Components
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: designTokens.borderRadius.xl,
          boxShadow: designTokens.shadows.lg,
          border: `1px solid ${designTokens.neutral[200]}`,
        },
      },
    },
    
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.md,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: designTokens.neutral[100],
          },
        },
      },
    },
  },
});

// Custom theme extensions for healthcare-specific components
export const healthcareTheme = {
  ...muiTheme,
  healthcare: {
    // Pain level colors
    painLevels: {
      low: {
        color: designTokens.semantic.success,
        background: designTokens.secondary[100],
      },
      medium: {
        color: designTokens.semantic.warning,
        background: '#fef3c7',
      },
      high: {
        color: designTokens.semantic.error,
        background: '#fecaca',
      },
    },
    // Session status colors
    sessionStatus: {
      completed: {
        color: designTokens.semantic.success,
        background: designTokens.secondary[100],
      },
      active: {
        color: designTokens.primary[600],
        background: designTokens.primary[100],
      },
      locked: {
        color: designTokens.neutral[500],
        background: designTokens.neutral[100],
      },
    },
    // Organic gradients
    gradients: {
      primary: `linear-gradient(135deg, ${designTokens.primary[500]} 0%, ${designTokens.primary[600]} 100%)`,
      secondary: `linear-gradient(135deg, ${designTokens.secondary[400]} 0%, ${designTokens.secondary[600]} 100%)`,
      organic: `linear-gradient(135deg, ${designTokens.secondary[200]} 0%, ${designTokens.primary[100]} 50%, ${designTokens.accent.yellow} 100%)`,
      warm: `linear-gradient(135deg, ${designTokens.accent.orange} 0%, ${designTokens.accent.pink} 100%)`,
      cool: `linear-gradient(135deg, ${designTokens.primary[400]} 0%, ${designTokens.accent.teal} 100%)`,
    },
    // Component variants
    components: {
      sessionCard: {
        padding: '24px',
        borderRadius: designTokens.borderRadius.xxl,
        boxShadow: designTokens.shadows.sm,
        border: `1px solid ${designTokens.neutral[200]}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: designTokens.shadows.lg,
          transform: 'translateY(-4px)',
        },
      },
      organicButton: {
        borderRadius: designTokens.borderRadius.full,
        padding: '12px 24px',
        background: `linear-gradient(135deg, ${designTokens.primary[500]} 0%, ${designTokens.primary[600]} 100%)`,
        color: '#ffffff',
        fontWeight: 500,
        '&:hover': {
          background: `linear-gradient(135deg, ${designTokens.primary[600]} 0%, ${designTokens.primary[700]} 100%)`,
          transform: 'translateY(-2px)',
          boxShadow: designTokens.shadows.md,
        },
      },
      
    },
  },

  
};



export default healthcareTheme;